import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { NewsItem } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';

const NewsPreview: React.FC = () => {
  const { t } = useScript();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadNews = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getNews()
      .then(items => { setNews(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || t('Грешка при учитавању.'));
        setLoading(false);
      });
  }, [t]);

  useEffect(() => { loadNews(); }, [loadNews]);

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm(t('Обрисати ову вест?'))) return;
    try {
      await api.deleteNews(token, id);
      setNews(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  return (
    <section className="news-section">
      <h2 className="news-heading">{t('НОВОСТИ МАНАСТИРА ГРАДАЦ')}</h2>

      {loading && (
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="state-block state-error" role="alert">
          <span>{error}</span>
          <button type="button" className="state-retry" onClick={loadNews}>
            {t('Покушај поново')}
          </button>
        </div>
      )}

      {!loading && !error && (
      <div className="news-grid">
        {news.slice(0, 3).map(n => (
          <article className="news-card" key={n.id}>
            <div className="news-image">
              <img
                src={n.imageUrl}
                alt={t(n.title)}
                onError={handleImgError}
                width={800}
                height={450}
                decoding="async"
                loading="lazy"
              />
              <div className="news-date">{t(n.dateLabel)}</div>
              {isAdmin && (
                <div className="news-admin-overlay">
                  <button className="news-admin-edit" onClick={() => navigate(`/novosti/${n.id}/uredi`)} title={t('Уреди')} aria-label={t('Уреди')}>✎</button>
                  <button className="news-admin-delete" onClick={() => handleDelete(n.id)} title={t('Обриши')} aria-label={t('Обриши')}>✕</button>
                </div>
              )}
            </div>
            <div className="news-body">
              <h3 className="news-title">{t(n.title)}</h3>
              <p className="news-excerpt">{t(n.excerpt)}</p>
              <Link to={`/novosti/${n.id}`} state={{ from: '/' }} className="news-more">{t('Читати даље')}</Link>
            </div>
          </article>
        ))}
      </div>
      )}

      <div className="news-actions">
        {isAdmin ? (
          <button type="button" className="news-all-btn" onClick={() => navigate('/novosti/nova')}>
            {t('+ Додај вест')}
          </button>
        ) : (
          <Link to="/novosti" className="news-all-btn news-all-link">
            {t('СВЕ НОВОСТИ')}
          </Link>
        )}
      </div>
    </section>
  );
};

export default NewsPreview;
